import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { OrganizationDto } from '../api/services/dto/user-managment-dto/organization-dto';
import { warrantService } from '../api/services/warrant.service';
import { setWorkspaceTenant } from '../api/api';

const WORKSPACE_KEY = 'workspace';

interface WorkspaceContextValue {
  workspace: OrganizationDto | null;
  setWorkspace: (org: OrganizationDto | null) => void;
  isAdmin: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspace: null,
  setWorkspace: () => {},
  isAdmin: false,
});

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const [workspace, setWorkspaceState] = useState<OrganizationDto | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // First fetch current user's organization (always needed as default)
    const initWorkspace = async () => {
      try {
        const [admin, currentOrg] = await Promise.all([
          warrantService.isCurrentUserAdmin(),
          warrantService.getCurrentUserOrganization(),
        ]);

        setIsAdmin(admin);

        if (admin) {
          // Check if admin has a previously saved workspace
          const saved = localStorage.getItem(WORKSPACE_KEY);
          if (saved) {
            try {
              const parsed: OrganizationDto = JSON.parse(saved);
              setWorkspaceState(parsed);
              setWorkspaceTenant(parsed.organizationId);
              return;
            } catch {
              localStorage.removeItem(WORKSPACE_KEY);
            }
          }
        }

        // Default: use current user's organization from JWT
        if (currentOrg) {
          setWorkspaceState(currentOrg);
          setWorkspaceTenant(currentOrg.organizationId);
        }
      } catch {
        setIsAdmin(false);
      }
    };

    initWorkspace();
  }, []);

  const setWorkspace = useCallback((org: OrganizationDto | null) => {
    setWorkspaceState(org);
    if (org) {
      localStorage.setItem(WORKSPACE_KEY, JSON.stringify(org));
      setWorkspaceTenant(org.organizationId);
    } else {
      localStorage.removeItem(WORKSPACE_KEY);
      setWorkspaceTenant(null);
    }
  }, []);

  return (
    <WorkspaceContext.Provider value={{ workspace, setWorkspace, isAdmin }}>
      {children}
    </WorkspaceContext.Provider>
  );
};
